import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';

import { useWorkspace } from '../contexts/WorkspaceContext';
import Logo from '../components/common/Logo';

export default function WorkspaceSetup() {
  const navigate = useNavigate();
  const { createWorkspace } = useWorkspace();

  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Please enter a workspace name');
      return;
    }

    if (trimmedName.length < 2) {
      setError('Workspace name must be at least 2 characters');
      return;
    }

    if (trimmedName.length > 50) {
      setError('Workspace name must be less than 50 characters');
      return;
    }

    setLoading(true);

    try {
      await createWorkspace(trimmedName);
      navigate('/vault');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create workspace';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Logo size="large" />
            </Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Name Your Workspace
            </Typography>
            <Typography color="text.secondary">
              Create a workspace to organize your passwords and share securely with others.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 3,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'primary.main',
                  color: 'white',
                }}
              >
                <BusinessIcon />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Workspace Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Personal, Family, My Company"
                  autoFocus
                  variant="outlined"
                  size="medium"
                  helperText="You can change this later"
                />
              </Box>
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || !name.trim()}
            >
              {loading ? 'Creating...' : 'Create Workspace'}
            </Button>
          </form>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              💡 <strong>Tip:</strong> A workspace lets you organize passwords and share them
              securely with family members or team colleagues.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

